import { Table, TableColumnsType } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  createAwardCourses,
  getAwardCourses,
  getAwards,
  updateAwardCourses,
} from '../../../api/award.api';
import { getSteps } from '../../../api/courses.api';
import Button from '../../../components/Button';
import Checkbox from '../../../components/Checkbox';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import Pagination from '../../../components/Pagination';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import config from '../../../lib/config';
import {
  IAwardItem,
  IGetAwardCourses,
  IGetAwards,
} from '../../../models/award.model';
import { IGetSteps, IStep } from '../../../models/courses.model';
import Split from 'react-split';

const ProgramAward = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const { successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetAwards>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetSteps>();
  const {
    data: res3,
    isLoading: isLoading3,
    error: error3,
    execute: execute3,
  } = useAxios<IGetAwardCourses>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios();

  const [page, setPage] = useState(1);
  const [award, setAward] = useState<IAwardItem>();
  const [selects, setSelects] = useState<any>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [isCreate, setIsCreate] = useState(true);

  useEffect(() => {
    execute(getAwards());
    execute2(getSteps());
  }, []);

  useEffect(() => {
    if (res3 && !isLoading3) {
      setSelects(res3.data?.courseCodes || []);
      setIsCreate(false);
    }
  }, [res3, isLoading3]);

  useEffect(() => {
    if (error3 && error3.response?.status === 404) {
      setSelects([]);
      setIsCreate(true);
    }
  }, [error3]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      return successNotification(t('common.message.save1'));
    }
  }, [submitRes, submitIsLoading]);

  const onSetSelects = (courseCode: number) => {
    if (selects.includes(courseCode)) {
      setSelects(selects.filter((item: any) => item !== courseCode));
      setIsSelectAll(false);
    } else {
      setSelects([...selects, courseCode]);
    }
  };

  const onSelectAll = () => {
    if (isSelectAll) {
      setSelects([]);
      setIsSelectAll(false);
    } else {
      const _selects = res2?.data?.map((item: IStep) =>
        Number(item.courseCode),
      );
      setSelects(_selects);
      setIsSelectAll(true);
    }
  };

  const onSubmit = () => {
    if (!award) return;

    if (isCreate) {
      submitExecute(
        createAwardCourses(award.awardCode, {
          courseCode: selects,
        }),
      );
    } else {
      submitExecute(
        updateAwardCourses(award.awardCode, {
          courseCode: selects,
        }),
      );
    }
  };

  const columns: TableColumnsType<IAwardItem> = [
    {
      title: t('program.award.column1'),
      dataIndex: 'name',
      key: 'name',
      align: 'center',
      sorter: (a, b) => a.name?.localeCompare(b.name),
    },
    {
      title: t('program.award.column2'),
      key: 'awardCode',
      dataIndex: 'awardCode',
      align: 'center',
      width: '100px',
      render: (value, record) => (
        <div className='comm-btn__area justify-content-center'>
          <Button
            className="btn-basic small mint"
            onClickCapture={(event) => {
              event.stopPropagation();
              setAward(record);
              setIsSelectAll(false);
              execute3(getAwardCourses(record.awardCode));
            }}
            text={t('program.award.button1')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      ),
    },
  ];

  return (
    <>
    <div className="content">
      {isLoading || isLoading2 || isLoading3 ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} ignoreNotFound />
      <ErrorResponse error={submitError} />
      <SimpleHeader
        parentMenu={t('common.menu.program')}
        currentMenu={t('program.award.title1')}
        iconFile="icon-program-big.svg"
        onlyTitle={true}
      />
      <Split 
        className='splite-divide'
        sizes={[50, 50]}
        minSize={100}
        expandToMin={false}
        gutterSize={10}
        gutterAlign="center"
        snapOffset={30}
        dragInterval={1}
        direction="horizontal"
        cursor="col-resize"
      >
        <div className='col-frame left_panel'>
          <div className="comm-bx__between">
            <div />
            <div className="comm-btn__area right">
              <Button
                className="btn-basic pink"
                onClick={() => history.push('/program/award/create')}
                text={t('program.award.button2')}
                writeAuthYn={writeAuthYn}
              />
            </div>
          </div>
          <Table
            className='comm-table__area'
            columns={columns}
            dataSource={res?.data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  history.push(`/program/award/${record.awardCode}`);
                },
              };
            }}
            rowKey="awardCode"
            rowClassName="pointer"
          />
          <div className="comm-bx__between">
            <div></div>
            <div className="common-paging center">
              <Pagination
                count={res?.data.length || 0}
                page={page}
                setPage={setPage}
              />
            </div>
            <div className="comm-btn__area right">
              <Button
                className="btn-basic pink"
                onClick={() => history.push('/program/award/create')}
                text={t('program.award.button2')}
                writeAuthYn={writeAuthYn}
              />
            </div>
          </div>
        </div>
        <div className='col-frame right_panel'>
          {award ? (
            <>
              <div className="comm-bx__between">
                <h3 className="sub-title">{award.name} {t('program.award.title2')}</h3>
              </div>
              <div className='comm-table__area'>
                <table>
                  <colgroup>
                    <col width="80px" />
                    <col width="*" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>
                        <Checkbox
                          checked={isSelectAll}
                          onChange={onSelectAll}
                          text=""
                        />
                      </th>
                      <th>{t('program.award.column3')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {res2?.data?.map(item => (
                      <tr>
                        <td>
                          <Checkbox
                            checked={selects.includes(item.courseCode)}
                            onChange={() =>
                              onSetSelects(Number(item.courseCode))
                            }
                            text=""
                          />
                        </td>
                        <td>{item.courseName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="comm-bx__space">
                <div className="comm-btn__area right">
                  <Button
                    className="btn-basic pink"
                    onClick={onSubmit}
                    text={t('common.button.save')}
                    writeAuthYn={writeAuthYn}
                  />
                </div>
              </div>
            </>
          ) : null}
        </div>
      </Split>
    </div>
    </>
  );
};

export default ProgramAward;
